import { Link, useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'

import type { Company as CompanyRecord } from '@/lib/api/endpoints/companies'
import type { Dimension, Recommendation } from '@/lib/api/endpoints/scorecard'
import { isForbiddenError, isNotFoundError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/auth-context'
import { canAddCompanies, canRescoreCompany, canUploadDocuments } from '@/lib/auth/roles'
import { ResearchProgress } from '@/pages/files/ResearchProgress'
import { useResearchTrigger } from '@/pages/files/use-research'
import {
  initialsOf,
  PRIORITY_COLOR,
  priorityOf,
  QUADRANT_BACKGROUND,
  QUADRANT_COLOR,
  QUADRANT_LABEL,
  subscoreBackground,
  subscoreColor,
  urgencyColor,
} from '@/lib/scoring/presentation'

import { TrendChart } from './TrendChart'
import { useCompanies } from './use-companies'
import { useScorecard, useTrend } from './use-company'

export function Company() {
  const { companyId = '' } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const [quarter, setQuarter] = useState<string | undefined>(undefined)

  const scorecard = useScorecard(companyId, quarter)

  const trend = useTrend(companyId, scorecard.data?.hasTrend === true)

  const companies = useCompanies()

  const { session } = useAuth()

  const canRescore = canRescoreCompany(session?.role ?? null)
  const canUpload = canUploadDocuments(session?.role ?? null)
  const research = useResearchTrigger(companyId)

  if (isForbiddenError(scorecard.error)) {
    return (
      <Shell>
        <div className="canopy-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 12 }}>
            That company is not in your portfolio.
          </div>
          <DeadEndAction companyId={companyId} companies={companies.data} />
        </div>
      </Shell>
    )
  }

  if (isNotFoundError(scorecard.error)) {
    return (
      <Shell subtitle="Company not found.">
        <div className="canopy-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 12 }}>
            That company doesn&rsquo;t exist or you don&rsquo;t have access.
          </div>
          <DeadEndAction companyId={companyId} companies={companies.data} />
        </div>
      </Shell>
    )
  }

  if (scorecard.isPending) return <Shell subtitle="Loading…" />

  if (scorecard.isError) {
    return <Shell subtitle="Could not load this company. Try again in a moment." error />
  }

  const { company, scores, dimensions, recommendations, quarters, snapshot } = scorecard.data
  const defensibility = dimensions.filter((d) => d.axis === 'defensibility')
  const readiness = dimensions.filter((d) => d.axis === 'readiness')
  const appliedQuarter = quarter ?? scorecard.data.quarter ?? ''
  const sortedCompanies = [...(companies.data ?? [])].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="canopy-page">
      <div className="canopy-page-header">
        <div>
          <h1 className="canopy-page-title">Company view</h1>
          <div className="canopy-page-sub">
            Full dimension breakdown with scoring justifications and recommended actions.
          </div>
        </div>
        <div className="canopy-ctrl-row">
          {sortedCompanies.length > 0 && (
            <select
              className="canopy-select-ctrl"
              value={companyId}
              onChange={(event) => {
                navigate(`/company/${encodeURIComponent(event.target.value)}`)
              }}
              aria-label="Company"
            >
              {sortedCompanies.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          )}
          {quarters.length > 0 && (
            <select
              className="canopy-select-ctrl"
              value={appliedQuarter}
              onChange={(event) => {
                setQuarter(event.target.value)
              }}
              aria-label="Quarter"
            >
              {quarters.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          )}
          {canUpload && (
            <Link className="canopy-btn" to={`/files?companyId=${encodeURIComponent(companyId)}`}>
              Upload document
            </Link>
          )}
          {canRescore && (
            <button
              type="button"
              className="canopy-btn"
              disabled={research.isRunning}
              onClick={() => {
                research.refresh(companyId)
              }}
            >
              {research.isRunning ? '↻ Running…' : 'Refresh scores'}
            </button>
          )}
        </div>
      </div>

      <ResearchProgress
        job={research.job.data}
        isRunning={research.isRunning}
        triggerError={research.error}
        onDismiss={research.dismiss}
      />

      <div className="canopy-company-hero">
        <div className="canopy-hero-top">
          <div
            className="canopy-hero-init"
            style={{
              background: scores ? QUADRANT_BACKGROUND[scores.quadrant] : 'var(--gray-100)',
              color: scores ? QUADRANT_COLOR[scores.quadrant] : 'var(--text-3)',
            }}
          >
            {initialsOf(company.name)}
          </div>
          <div>
            <div className="canopy-hero-name">{company.name}</div>
            <div className="canopy-hero-meta">
              {scores && (
                <span className={`canopy-tag canopy-tag-${scores.quadrant}`}>
                  {QUADRANT_LABEL[scores.quadrant]}
                </span>
              )}
              {company.sector && (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{company.sector}</span>
              )}
              {appliedQuarter && (
                <>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>·</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{appliedQuarter}</span>
                </>
              )}
              {!scores && (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No score yet</span>
              )}
            </div>
          </div>
        </div>

        <div className="canopy-hero-scores">
          <div className="canopy-hero-score">
            <div className="canopy-hero-score-label">Urgency</div>
            <div
              className="canopy-hero-score-val"
              style={{ color: scores ? urgencyColor(scores.urgency) : 'var(--text-3)' }}
            >
              {scores ? scores.urgency : '—'}
            </div>
          </div>
          <div className="canopy-hero-score">
            <div className="canopy-hero-score-label">Defensibility</div>
            <div className="canopy-hero-score-val" style={{ color: 'var(--green-600)' }}>
              {scores ? scores.def : '—'}
              {scores && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>/100</span>}
            </div>
          </div>
          <div className="canopy-hero-score">
            <div className="canopy-hero-score-label">Readiness</div>
            <div className="canopy-hero-score-val" style={{ color: 'var(--blue-600)' }}>
              {scores ? scores.rdy : '—'}
              {scores && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>/100</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="canopy-two-col">
        <div className="canopy-card canopy-trend-card">
          <div className="canopy-card-title">
            Score trend
            <span className="canopy-card-hint">composite history</span>
          </div>
          <TrendPanel
            hasTrend={scorecard.data.hasTrend}
            isPending={trend.isPending}
            isError={trend.isError}
            points={trend.data?.points ?? []}
            urgencyLevels={trend.data?.urgencyLevels ?? []}
          />
        </div>

        <div className="canopy-card">
          <div className="canopy-card-title">
            Recommended actions
            <span className="canopy-card-hint">
              {recommendations.length === 0
                ? 'no recommendations yet'
                : `${String(recommendations.length)} action${recommendations.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {recommendations.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '12px 0' }}>
              This snapshot pre-date the recommendations field - re-run research to populated it
            </div>
          ) : (
            <div className="canopy-rec-list">
              {recommendations.map((rec, index) => (
                <RecommendationRow key={`${rec.action}-${String(index)}`} recommendation={rec} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="canopy-two-col">
        <DimensionsCard
          title="AI defensibility"
          composite={scores?.def}
          dimensions={defensibility}
          tint="var(--green-400)"
          hasSnapshot={snapshot != null}
        />
        <DimensionsCard
          title="AI readiness"
          composite={scores?.rdy}
          dimensions={readiness}
          tint="var(--blue-400)"
          hasSnapshot={snapshot != null}
        />
      </div>
    </div>
  )
}

function DeadEndAction({
  companyId,
  companies,
}: {
  companyId: string
  companies: CompanyRecord[] | undefined
}) {
  const { session } = useAuth()

  if (companies === undefined) return null

  const others = [...companies]
    .filter((option) => option.id !== companyId)
    .sort((a, b) => a.name.localeCompare(b.name))
  const first = others[0]

  if (first) {
    return (
      <Link
        to={`/company/${encodeURIComponent(first.id)}`}
        className="canopy-btn canopy-btn-primary"
      >
        {others.length === 1 ? `Open ${first.name} →` : 'Open a company →'}
      </Link>
    )
  }

  if (canAddCompanies(session?.role ?? null)) {
    return (
      <Link to="/admin/companies" className="canopy-btn canopy-btn-primary">
        Add companies →
      </Link>
    )
  }

  return (
    <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.5 }}>
      Company access is the growth team&rsquo;s to grant. Ask them to add you to a company, and its
      scorecard will open here.
    </div>
  )
}

function Shell({
  children,
  subtitle = 'Full dimension breakdown with scoring justifications and recommended actions.',
  error = false,
}: {
  children?: React.ReactNode
  subtitle?: string
  error?: boolean
}) {
  return (
    <div className="canopy-page">
      <div className="canopy-page-header">
        <div>
          <h1 className="canopy-page-title">Company view</h1>
          <div
            className="canopy-page-sub"
            {...(error ? { style: { color: 'var(--red-600)' }, role: 'alert' } : {})}
          >
            {subtitle}
          </div>
        </div>
      </div>
      {children}
    </div>
  )
}

function TrendPanel({
  hasTrend,
  isPending,
  isError,
  points,
  urgencyLevels,
}: {
  hasTrend: boolean
  isPending: boolean
  isError: boolean
  points: React.ComponentProps<typeof TrendChart>['points']
  urgencyLevels: NonNullable<React.ComponentProps<typeof TrendChart>['urgencyLevels']>
}) {
  if (!hasTrend) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '12px 0' }}>
        Need at least two snapshots to chart a trend. One more snapshot will start the trend line.
      </div>
    )
  }

  if (isPending) return <div className="canopy-empty-state">Loading trend…</div>
  if (isError) return <div className="canopy-empty-state">Could not load the trend.</div>

  if (points.length < 2) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '12px 0' }}>
        Need at least two snapshots to chart a trend. One more snapshot will start the trend line.
      </div>
    )
  }

  return <TrendChart points={points} urgencyLevels={urgencyLevels} />
}

function RecommendationRow({ recommendation }: { recommendation: Recommendation }) {
  const priority = priorityOf(recommendation.priority)

  return (
    <div className="canopy-rec-item">
      <div className="canopy-rec-dot" style={{ background: PRIORITY_COLOR[priority] }} />

      <div style={{ flex: 1 }}>
        <div className="canopy-rec-action">{recommendation.action}</div>
        <div className="canopy-rec-meta">
          <span className={`canopy-tag canopy-tag-${priority}`}>{priority}</span>
          {recommendation.owner && <span>Owner: {recommendation.owner}</span>}
          {recommendation.deadline && <span>· {recommendation.deadline}</span>}
        </div>
      </div>
    </div>
  )
}

function DimensionsCard({
  title,
  composite,
  dimensions,
  tint,
  hasSnapshot,
}: {
  title: string
  composite: number | undefined
  dimensions: readonly Dimension[]
  tint: string
  hasSnapshot: boolean
}) {
  const weights = dimensions.map((d) => d.weightPercent).join('/')

  return (
    <div className="canopy-card">
      <div className="canopy-card-title">
        <span>
          {title}
          {composite != null && ` — ${String(composite)}/100`}
        </span>
        {weights !== '' && <span className="canopy-card-hint">weights: {weights}%</span>}
      </div>
      {dimensions.map((dimension) => (
        <DimensionRow
          key={dimension.field}
          dimension={dimension}
          tint={tint}
          hasSnapshot={hasSnapshot}
        />
      ))}
    </div>
  )
}

function DimensionRow({
  dimension,
  tint,
  hasSnapshot,
}: {
  dimension: Dimension
  tint: string
  hasSnapshot: boolean
}) {
  const value = dimension.value ?? null
  const justification = dimension.evidence[0]?.text ?? null

  return (
    <div className="canopy-dim-row">
      <div className="canopy-dim-row-top">
        <span className="canopy-dim-label">{dimension.label}</span>
        <span className="canopy-dim-weight">{dimension.weightPercent}%</span>
        {value !== null && (
          <span
            className="canopy-dim-score-badge"

            style={{ background: subscoreBackground(value), color: subscoreColor(value) }}
          >
            {value}/5
          </span>
        )}
      </div>
      <div className="canopy-dim-bar-row">
        <div className="canopy-dim-bar-track">
          <div
            className="canopy-dim-bar-fill"
            style={{ width: `${String((value ?? 0) * 20)}%`, background: tint }}
          />
        </div>
        <div className="canopy-dim-pips">
          {[1, 2, 3, 4, 5].map((pip) => (
            <div
              className="canopy-dim-pip"
              key={pip}
              style={value !== null && pip <= value ? { background: tint } : undefined}
            />
          ))}
        </div>
      </div>

      {justification !== null ? (
        <div className="canopy-dim-justification">{justification}</div>
      ) : (
        <div
          className="canopy-dim-justification"
          style={{ color: 'var(--text-3)', fontStyle: 'italic' }}
        >
          {hasSnapshot ? 'No evidence recorded for this dimension.' : 'No score yet.'}
        </div>
      )}

      {dimension.rubricLevelText && (
        <div className="canopy-dim-rubric">{dimension.rubricLevelText}</div>
      )}
    </div>
  )
}
